import styled, { css } from "styled-components/macro";
import { Alert } from "@mui/material";
import { TextField } from "../shared/TextField/TextField";
import { CheckMark } from "../../assets/iconComponents/CheckMark";
import { Password } from "../shared/TextField/Password";

const responsiveFormWidth = css`
  max-width: 100%;
  width: 327px;
  @media (min-width: 601px) {
    width: 421px;
  }
  @media (min-width: 1201px) {
    width: 447px;
  }
`;

export const CreateAccountLoginMessageContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 10px;
  justify-content: center;
  padding-top: 16px;
  padding-bottom: 10px;
`;

export const FormFieldInput = styled.div`
  ${responsiveFormWidth}
`;

export const CreateAccountDesktopContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background-color: ${({ theme }) => theme.white};
`;

export const CreateAccountPasswordRequirmentsTitle = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  text-align: left;
  grid-gap: 7px;
`;

export const CreateAccountPasswordRequirmentsContainer = styled.div<{
  flat?: boolean;
}>`
  display: grid;
  grid-template-rows: ${({ flat }) => (flat ? undefined : "auto auto")};
  grid-template-columns: ${({ flat }) => (flat ? "1fr 1fr" : undefined)};
  place-items: start;
  grid-gap: 7px;
`;

export const CreateAccountPasswordRequirmentsRow = styled.div`
  display: inline-grid;
  grid-template-columns: auto auto;
  place-items: start;
  grid-gap: 7px;
`;

export const CreateAccountStyledCheckMark = styled(CheckMark)<{
  $valid: boolean;
}>`
  margin-top: 4px;
  path {
    fill: ${({ $valid, theme }) =>
      $valid ? theme.greenDark : theme.greyLight} !important;
  }
`;

export const CreateAccountButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export const CreateAccountCenterContainer = styled.div`
  max-width: 100%;
  min-width: 0;
  position: relative;
  display: flex;
  background-color: white;
  height: 100%;
  @media (min-width: 1201px) {
    height: auto;
    min-height: 100vh;
  }
`;

export const CreateAccountCenterFormContainer = styled.div`
  display: grid;
  margin: auto;
  grid-gap: 5px;
  padding: 10px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  place-items: center;
  grid-template-rows: auto auto auto auto auto 1fr;
`;

export const CreateAccountCenterFormPreTestTitle = styled.div`
  font-style: normal;
  font-weight: 800;
  font-size: 24px;
  line-height: 32px;
  color: ${({ theme }) => theme.greyBase};
`;

export const CreateAccountCenterContainerPaddedTop = styled.div`
  margin: auto;
  max-width: 480px;
  width: 100%;
  display: grid;
  grid-gap: 5px;
  padding: 10px;
  place-items: center;
  align-content: center;
  text-align: center;
  box-sizing: border-box;
`;

export const CreateAccountDesktopFormCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
`;

export const CreateAccountDesktopColorBackground = styled.div`
  position: relative;
  min-height: 100vh;
  height: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.loginBrandPanel};
`;

export const CreateAccountDesktopGradientLayer = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.loginHeroGradientDesktop};
  transform: rotate(180deg) scaleX(-1);
  transform-origin: center center;
`;

export const CreateAccountDesktopBrandPanel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  z-index: 1;
  padding: 0 40px 0 clamp(24px, 4vw, 55px);
  box-sizing: border-box;
  pointer-events: none;

  & img {
    pointer-events: auto;
  }
`;

export const CreateAccountDesktopBrandLockup = styled.img`
  display: block;
  height: 93px;
  width: auto;
  max-width: min(228px, 42vw);
  flex-shrink: 0;
`;

export const CreateAccountNonDesktopShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background-color: ${({ theme }) => theme.white};
`;

export const CreateAccountHeroBand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: calc(32px + env(safe-area-inset-top, 0px)) 16px 32px;
  background: ${({ theme }) => theme.loginHeroGradientMobile};
  @media (min-width: 601px) {
    padding: calc(40px + env(safe-area-inset-top, 0px)) 24px 40px;
    background: ${({ theme }) => theme.loginHeroGradient};
  }
`;

export const CreateAccountHeroLockupImg = styled.img`
  display: block;
  height: 28px;
  width: auto;
  max-width: 100%;
  @media (min-width: 601px) {
    height: 40px;
  }
`;

export const CreateAccountNonDesktopContents = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
`;

export const CreateAccountInput = styled(TextField)`
  width: 100%;

  .MuiInputLabel-asterisk {
    color: ${({ theme }) => theme.redDark};
    margin-left: 2px;
  }
`;

export const CreateAccountDateOfBirthField = styled(CreateAccountInput)`
  // Maintain AA contrast for the date-format hint.
  && input::placeholder {
    color: rgba(0, 0, 0, 0.6);
    opacity: 1;
  }
`;

export const CreateAccountHeadingGroup = styled.div`
  padding-top: 40px;
  @media (min-width: 601px) {
    padding-top: 75px;
  }
  display: grid;
  place-items: center;
  text-align: center;
`;

export const VerifyEmailSendAgainInput = styled(TextField)`
  width: 100%;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.greyLight};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.greenMedium};
  padding: 14px;
  padding-top: 17px;
  border-radius: 5px;
  margin-bottom: 40px;
`;

export const Spinner = styled.div`
  text-align: center;
  margin-top: 15px;
  margin-bottom: 15px;
  img {
    width: 50px;
  }
`;

export const CreateAccountFormStyled = styled.div<{
  isPasswordSection?: boolean;
}>`
  display: grid;
  grid-template-rows: ${({ isPasswordSection }) =>
    isPasswordSection ? "20px" : "40px"};
  grid-row-gap: ${({ isPasswordSection }) =>
    isPasswordSection ? "20px" : "12px"};
  @media (min-width: 601px) {
    grid-row-gap: 20px;
  }
  padding-top: 12px;
  padding-bottom: 40px;
  place-items: center;
`;

// Language preference sits with the title (not the field group) per design:
// a narrow, centered, underline (standard-variant) select.
export const CreateAccountLanguageSelect = styled(TextField)`
  width: 150px;

  /* Match design which has a slightly larger label sitting closer to the value
     (default standard-variant shrink is 0.75 + a 6px gap). */
  && .MuiInputLabel-shrink {
    transform: translate(0, 1px) scale(0.875);
  }

  /* The label's default overflow: hidden clips descenders (the "g" in
     "Language"); let it paint fully without any layout/spacing change. */
  && .MuiInputLabel-root {
    overflow: visible;
  }
`;

export const CreateAccountPassword = styled(Password)`
  width: 100%;
`;

export const CreateAccountPasswordInfoContainer = styled.div`
  width: 100%;
`;

export const CreateAccountPasswordValidationContainer = styled.div`
  width: 100%;
  margin-top: 30px;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

export const CreateAccountPrivacyPolicy = styled.div`
  margin-top: 5px;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.greyLight};

  a {
    color: ${({ theme }) => theme.greyLight};
  }
`;

export const CreateAccountMismatchMessage = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.redPastel};
  border-left: 3px solid ${({ theme }) => theme.redDark};
  padding: 20px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  color: ${({ theme }) => theme.redDark};
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;

  a {
    color: inherit;
  }
`;

export const MismatchErrorTitle = styled.strong`
  font-family: bold;
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
`;

export const MismatchErrorBody = styled.p`
  margin: 10px 0 0;

  & + & {
    margin-top: 20px;
  }
`;

export const CreateAccountGuidanceBanner = styled(Alert)`
  && {
    box-sizing: border-box;
    ${responsiveFormWidth}
    max-width: 100%;
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 12px;
    align-items: center;
    padding: 20px;
    border-radius: 0;
    border-left: 3px solid ${({ theme }) => theme.blueDarker};
    background: ${({ theme }) => theme.blueSubtle};
    color: ${({ theme }) => theme.blueDarker};
    font-family: inherit;
    letter-spacing: normal;

    .MuiAlert-icon {
      opacity: 1;
      height: 28px;
      padding: 0;
      margin: 0;
      align-items: center;
    }
    .MuiAlert-icon svg {
      display: block;
    }
    .MuiAlert-message {
      display: contents;
    }
    .MuiAlertTitle-root {
      margin: 0;
      font-family: bold;
      font-size: 18px;
      font-weight: 700;
      line-height: 28px;
    }
    .MuiAlert-message > div:last-child {
      grid-column: 1 / -1;
      font-size: 14px;
      line-height: 20px;
    }
    p {
      margin: 0;
    }
    p + p {
      margin-top: 10px;
    }
  }
`;

export const CreateAccountStepContainer = styled(
  CreateAccountCenterFormContainer,
)<{
  $verificationStep: boolean;
}>`
  ${({ $verificationStep }) =>
    $verificationStep &&
    css`
      display: flex;
      flex-direction: column;
      gap: 32px;
      padding: 10px 10px 25px;

      ${CreateAccountFormStyled} {
        display: flex;
        flex-direction: column;
        gap: 32px;
        padding: 0 0 64px;
      }

      ${CreateAccountMismatchMessage} {
        width: 100%;
      }
    `}
`;
